import { CardColor, GamePhase, GameState, Player } from '../types'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}

export const chooseChromabowColor = (state: GameState, playerId: number, color: CardColor): GameState => {
  log('chooseChromabowColor', `Player ${playerId} chose to draw a ${color} card.`)
  if (state.phase !== GamePhase.AWAITING_CHROMABOW_CHOICE || state.chromabowInitiatorId !== playerId) {
    log('chooseChromabowColor', 'Invalid state for this action.')
    return state
  }
  let newState = JSON.parse(JSON.stringify(state))
  const player = newState.players.find((p: Player) => p.id === playerId)!
  if (newState.colorDecks[color]?.length > 0) {
    const cardToDraw = newState.colorDecks[color].pop()!
    cardToDraw.ownerId = player.id
    player.hand.push(cardToDraw)
    newState.log.push(`${player.name} draws a card from the ${color} deck.`)
  } else {
    newState.log.push(`The ${color} deck is empty!`)
  }
  newState.phase = GamePhase.Combat
  newState.turnPlayerId = playerId  // Keep turn with the chromabow initiator
  newState.chromabowInitiatorId = undefined
  return newState
}

export const chooseHibernationColor = (state: GameState, playerId: number, color: CardColor): GameState => {
  log('chooseHibernationColor', `Player ${playerId} chose to draw a ${color} card from Hibernation.`)
  if (state.phase !== GamePhase.AWAITING_HIBERNATION_CHOICE || state.hibernationInitiatorId !== playerId) {
    log('chooseHibernationColor', 'Invalid state for this action.')
    return state
  }
  let newState = JSON.parse(JSON.stringify(state))
  const player = newState.players.find((p: Player) => p.id === playerId)!
  if (newState.colorDecks[color]?.length > 0) {
    const cardToDraw = newState.colorDecks[color].pop()!
    cardToDraw.ownerId = player.id
    player.hand.push(cardToDraw)
    newState.log.push(`${player.name} draws a card from the ${color} deck due to Hibernation.`)
  } else {
    newState.log.push(`The ${color} deck is empty!`)
  }

  // Check if Chromabow choice is pending (e.g., Chromabow was partially countered by Hibernation)
  if (newState.chromabowInitiatorId) {
    newState.phase = GamePhase.AWAITING_CHROMABOW_CHOICE
    log('chooseHibernationColor', `Transitioning to Chromabow choice for player ${newState.chromabowInitiatorId}`)
  } else {
    newState.phase = GamePhase.Combat
  }
  // Don't change turnPlayerId - turn should remain with whoever had it (the card caster, not the choice-maker)
  newState.hibernationInitiatorId = undefined
  return newState
}

export const chooseBurglaryCard = (state: GameState, playerId: number, cardId: string): GameState => {
  log('chooseBurglaryCard', `Player ${playerId} chose a card to steal.`)
  if (state.phase !== GamePhase.AWAITING_BURGLARY_CHOICE || state.burglaryInitiatorId !== playerId) {
    log('chooseBurglaryCard', 'Invalid state for this action.')
    return state
  }
  let newState = JSON.parse(JSON.stringify(state))
  const attacker = newState.players.find((p: Player) => p.id === playerId)!
  const defender = newState.players.find((p: Player) => p.id === newState.burglaryTargetId)!
  const stolenIndex = defender.hand.findIndex(c => c.id === cardId)
  if (stolenIndex === -1) return newState
  const [stolenCard] = defender.hand.splice(stolenIndex, 1)
  stolenCard.ownerId = attacker.id
  attacker.hand.push(stolenCard)
  newState.log.push(`${attacker.name} steals a card from ${defender.name}.`)
  newState.phase = GamePhase.Combat
  newState.turnPlayerId = playerId  // Keep turn with the burglary initiator
  newState.burglaryInitiatorId = undefined
  newState.burglaryTargetId = undefined
  return newState
}

